/* Entry point: wires hooks, toolbar, boot sequence. */
import { $, hooks, redo, state, undo } from './state.ts';
import { clamp, getUnits, IN_PER_CM, setUnits, type Units } from './utils.ts';
import { setSnapshotInterval } from './versions.ts';
import { coachHide, coachReset, coachShow } from './coach.ts';
import { installA11y } from './a11y.ts';
import { formatReport, ReportQueue } from './report.ts';
import { cloudReady, sendErrorReport } from './cloud.ts';

/* uncaught errors: trimmed, de-duplicated, buffered, sent when the cloud is reachable */
const reports = new ReportQueue((() => { try { return localStorage; } catch { return null; } })());
const BUILD = (document.querySelector('script[src*="app.js"]') as HTMLScriptElement | null)?.src.split('?')[1] || 'dev';
function report(e: { message?: unknown; stack?: unknown; source?: string }): void {
  const rec = formatReport(e, { page: location.pathname + location.search, layout: state.name, ua: navigator.userAgent, build: BUILD });
  if (reports.add(rec)) flushReports();
}
let flushing = false;
async function flushReports(): Promise<void> {
  if (flushing || !reports.pending.length || !cloudReady()) return;
  flushing = true;
  try { await reports.flush(r => sendErrorReport(r as unknown as Record<string, unknown>)); } finally { flushing = false; }
}
window.addEventListener('error', ev => report({ message: ev.message, stack: ev.error?.stack, source: 'error' }));
window.addEventListener('unhandledrejection', ev => report({ message: ev.reason?.message ?? String(ev.reason), stack: ev.reason?.stack, source: 'unhandledrejection' }));
import { CSS_W, CSS_H, drawNow, exportPNG, fitView, renderExportCanvas, requestDraw, resize, setViewOpt, zoomAt } from './render.ts';
import { renderPanel } from './panel.ts';
import { buildRail, HINTS, setTool } from './tools.ts';
import { confirmBox, createLayout, importStarter, initStore, listLayouts, loadLayout, markDirty, markDirtySoft, notice, openInvite, openShare, saveNow, setBadge, showNudge } from './net.ts';
import { solveConstraints } from './solver.ts';
import './input.ts';

/* cross-cutting callbacks for low-level modules (keeps the module graph acyclic) */
hooks.markDirty = markDirty;
hooks.markDirtySoft = markDirtySoft;
hooks.renderPanel = renderPanel;
hooks.requestDraw = requestDraw;
hooks.saveNow = saveNow;
hooks.setTool = setTool;
hooks.coach = coachShow;
$('#coachOk').addEventListener('click', coachHide);
hooks.notify = msg => { notice(msg); };
hooks.confirm = (msg, ok) => confirmBox(msg, ok || 'Remove', true);

/* toolbar buttons */
$('#undoBtn').addEventListener('click', () => { undo(); requestDraw(); });
$('#redoBtn').addEventListener('click', () => { redo(); requestDraw(); });
$('#zoomIn').addEventListener('click', () => zoomAt({ x: CSS_W / 2, y: CSS_H / 2 }, clamp(state.view.zoom * 1.25, .25, 60)));
$('#zoomOut').addEventListener('click', () => zoomAt({ x: CSS_W / 2, y: CSS_H / 2 }, clamp(state.view.zoom / 1.25, .25, 60)));
$('#fitBtn').addEventListener('click', fitView);
$('#zoomPct').addEventListener('click', fitView);
$('#snapSel').addEventListener('change', e => { state.snapStep = parseFloat((e.target as HTMLSelectElement).value); });

/* units: the doc is always inches; this only changes what people see and type */
const SNAPS: Record<Units, [number, string][]> = {
  ft: [[0.5, '½″'], [1, '1″'], [3, '3″'], [6, '6″'], [12, '1′'], [0, 'off']],
  m: [[1 * IN_PER_CM, '1 cm'], [5 * IN_PER_CM, '5 cm'], [10 * IN_PER_CM, '10 cm'], [50 * IN_PER_CM, '50 cm'], [0, 'off']],
};
const DEFAULT_SNAP: Record<Units, number> = { ft: 1, m: 1 * IN_PER_CM };
function renderSnapOptions(): void {
  const sel = $('#snapSel') as HTMLSelectElement;
  sel.innerHTML = '';
  for (const [v, label] of SNAPS[getUnits()]) {
    const o = document.createElement('option'); o.value = String(v); o.textContent = label; sel.appendChild(o);
  }
  state.snapStep = DEFAULT_SNAP[getUnits()];
  sel.value = String(state.snapStep);
}
export function applyUnits(u: Units): void {
  setUnits(u);
  try { localStorage.setItem('playout:units', u); } catch { /* private mode */ }
  ($('#unitsSel') as HTMLSelectElement).value = u;
  renderSnapOptions();
  renderPanel(); requestDraw();
}
$('#unitsSel').addEventListener('change', e => applyUnits((e.target as HTMLSelectElement).value as Units));
(() => { let u: Units = 'ft'; try { u = (localStorage.getItem('playout:units') as Units) || 'ft'; } catch { /* ignore */ } applyUnits(u === 'm' ? 'm' : 'ft'); })();
$('#exportBtn').addEventListener('click', exportPNG);

new ResizeObserver(resize).observe($('#canvasWrap'));

/* expose internals for headless tests / debugging */
Object.assign(window as any, { state, saveNow, solveConstraints, loadLayout, createLayout, listLayouts, requestDraw, renderPanel, setTool, setSnapshotInterval, coachReset, renderExportCanvas, __reports: reports, showNudge, drawNow });
$('#emptyRoomBtn').addEventListener('click', () => setTool('room'));

/* small screens: the panel is a bottom sheet behind a status-bar tab that
   names what's in it ("Room ▴", "Wall ▴", "Start your plan ▴") */
const mainEl = $('#main'), panelTab = $('#panelTab');
const updatePanelTab = () => {
  const title = $('#panel').querySelector('h3')?.textContent || 'Panel';
  panelTab.textContent = `${title} ${mainEl.classList.contains('sheetOpen') ? '▾' : '▴'}`;
};
panelTab.addEventListener('click', () => { mainEl.classList.toggle('sheetOpen'); updatePanelTab(); });
new MutationObserver(updatePanelTab).observe($('#panel'), { childList: true });
new MutationObserver(updatePanelTab).observe(mainEl, { attributes: true, attributeFilter: ['class'] });

/* first visit: whatever opened (usually the sample plan), offer a clean start */
const welcomeBar = $('#welcomeBar');
const WELCOMED = 'playout:welcomed';
function dismissWelcome(): void {
  welcomeBar.classList.remove('show');
  try { localStorage.setItem(WELCOMED, '1'); } catch { /* private mode */ }
}
async function startOwnPlan(): Promise<void> {
  dismissWelcome();
  const taken = new Set((await listLayouts()).map(l => l.name));
  let name = 'My plan';
  for (let i = 2; taken.has(name); i++) name = `My plan ${i}`;
  await createLayout(name);
  setTool('room');
}
$('#welcomeStart').addEventListener('click', startOwnPlan);
$('#welcomeExplore').addEventListener('click', dismissWelcome);
function maybeWelcome(): void {
  let seen = true;
  try { seen = !!localStorage.getItem(WELCOMED); } catch { /* private mode: don't nag */ }
  if (!seen && !state.shareView) welcomeBar.classList.add('show');
}

/* ---- ?perf=1: live diagnostics overlay (frame health, drag state, errors) ---- */
if (new URLSearchParams(location.search).has('perf')) {
  const box = document.createElement('div');
  box.style.cssText = 'position:fixed;right:10px;bottom:46px;z-index:9999;background:rgba(0,0,0,.82);' +
    'color:#8f8;font:11px/1.5 ui-monospace,Menlo,monospace;padding:8px 10px;border-radius:8px;' +
    'pointer-events:none;white-space:pre;min-width:230px';
  document.body.appendChild(box);
  let lastErr = '';
  window.addEventListener('error', e => { lastErr = e.message; });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) =>
    { lastErr = String(e.reason).slice(0, 120); });
  const gaps: number[] = [];
  let last = performance.now();
  import('./input.ts').then(({ perf }) => {
    const tick = () => {
      const now = performance.now();
      gaps.push(now - last); last = now;
      while (gaps.length > 60) gaps.shift();
      const worst = Math.max(...gaps);
      const fps = 1000 / (gaps.reduce((a, b) => a + b, 0) / gaps.length);
      const evAge = perf.lastEventAt ? now - perf.lastEventAt : 0;
      box.textContent =
        `fps ${fps.toFixed(0)}  worst ${worst.toFixed(0)}ms\n` +
        `move handler ${perf.moveMs.toFixed(1)}ms  (${perf.moves} moves)\n` +
        `drag: ${(state.drag && state.drag.kind) || '—'}\n` +
        `last event: ${perf.lastEvent} ${evAge > 500 ? '(' + (evAge / 1000).toFixed(1) + 's ago!)' : ''}\n` +
        `cancelled drags: ${perf.cancels}` +
        (lastErr ? `\nERROR: ${lastErr}` : '');
      box.style.color = lastErr ? '#f88' : worst > 80 ? '#fc6' : '#8f8';
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/* bottom-bar View popup (show/hide annotations) */
const viewMenu = $('#viewMenu');
$('#viewBtn').addEventListener('click', e => { e.stopPropagation(); viewMenu.classList.toggle('show'); });
document.addEventListener('click', e => {
  if (!viewMenu.contains(e.target as Node) && (e.target as HTMLElement).id !== 'viewBtn') viewMenu.classList.remove('show');
});
for (const key of ['dims', 'badges', 'labels', 'areas', 'below'] as const) {
  const chk = $('#view' + key[0].toUpperCase() + key.slice(1)) as HTMLInputElement;
  chk.checked = state.show[key];
  chk.addEventListener('change', () => setViewOpt(key, chk.checked));
}

/* help dialog (topbar ? — the "?" key toggles it too, see input.ts) */
const helpModal = $('#helpModal');
$('#helpBtn').addEventListener('click', () => helpModal.classList.toggle('show'));
$('#helpClose').addEventListener('click', () => helpModal.classList.remove('show'));
helpModal.addEventListener('click', e => { if (e.target === helpModal) helpModal.classList.remove('show'); });

installA11y();

(async function boot() {
  buildRail();
  setTool('select');
  resize();
  const shareParam = new URLSearchParams(location.search).get('share');
  if (shareParam) $('#bootMsg').textContent = 'Opening the shared layout…';
  try {
    await initStore();   // firebase init (if configured) + first auth state + backend
    const params = new URLSearchParams(location.search);
    const share = shareParam;
    const invite = params.get('invite');
    if (share && await openShare(share)) {
      // viewing someone's share link — nothing else to load
    } else if (invite && await openInvite(invite)) {
      // accepted (or waiting for sign-in, which accepts it)
    } else {
      const list = await listLayouts();
      const want = params.get('layout');
      if (want && list.some(l => l.name === want)) await loadLayout(want);
      else if (want && await importStarter(want)) { /* demo link: layout came from the bundled starters */ }
      else if (list.length) await loadLayout(list[0].name);
      else await createLayout('home');
    }
  } catch {
    setBadge('Offline', 'offline');
  }
  $('#boot').classList.add('hide');
  $('#hint').textContent = HINTS.select;
  if (shareParam && !state.shareView)
    notice('This share link is no longer active — the owner may have stopped sharing it. You’re looking at your own plans instead.');
  else maybeWelcome();
  flushReports();   // anything buffered from an earlier, offline session
})();
